import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Lock, Unlock, Loader2, Timer, EyeOff, Trash2 } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { API } from '../../lib/constants';
import { formatApiErrorDetail } from '../../context/AuthContext';

const GUARANTEES = [
  { icon: EyeOff, title: 'Selfies never stored', body: 'Attendee selfies are converted in memory to a 128-number signature and discarded.' },
  { icon: Timer, title: '30-day auto-purge', body: 'Every signature is deleted automatically 30 days after registration — no action needed.' },
  { icon: Trash2, title: 'Guest self-service', body: 'Attendees can permanently delete their own data from their gallery page at any time.' },
];

export const PrivacyPanel = ({ onChanged, isDemo = false }) => {
  const [info, setInfo] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/admin/event`).then((r) => setInfo(r.data)).catch(() => toast.error('Failed to load privacy status'));
  useEffect(() => { load(); }, []);

  const closeEvent = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/event/close`);
      toast.success(`Event closed — ${data.purged_signatures} face signature(s) purged`);
      setConfirm(false);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to close event');
    } finally {
      setBusy(false);
    }
  };

  const reopen = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/event/reopen`);
      toast.success('Registration reopened');
      await load();
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const closed = info?.event_status === 'closed';

  return (
    <div className="space-y-6" data-testid="privacy-panel">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {GUARANTEES.map(({ icon: Icon, title, body }, i) => (
          <div key={title} className={`card p-5 fade-up fade-up-delay-${i}`}>
            <span className="w-9 h-9 bg-lime text-ink flex items-center justify-center mb-4"><Icon className="w-4 h-4" /></span>
            <p className="font-display font-bold uppercase tracking-wider text-sm text-white">{title}</p>
            <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{body}</p>
          </div>
        ))}
      </div>

      <div className={`p-7 ${closed ? 'card' : 'card-brutal-lime'}`} data-testid="event-status-card">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="h-display text-lg">Event status</h2>
              <span className={closed ? 'badge-muted' : 'badge-lime'} data-testid="event-status-badge">{closed ? 'Closed' : 'Open'}</span>
            </div>
            <p className="text-sm text-slate-400 mt-2 max-w-xl leading-relaxed">
              {closed
                ? `Registration is closed and all signatures were purged${info?.event_closed_at ? ` on ${new Date(info.event_closed_at).toLocaleString()}` : ''}. Existing gallery links keep working.`
                : 'Closing the event stops new registrations and permanently purges every attendee face signature right now. Galleries stay available for downloads.'}
            </p>
            <div className="mt-4 flex flex-wrap gap-3 font-mono text-[11px] uppercase tracking-[0.2em]">
              <span className="text-slate-500">Active signatures <span className="text-white font-bold" data-testid="active-signatures">{info?.active_signatures ?? '—'}</span></span>
              <span className="text-slate-500">Purged <span className="text-white font-bold" data-testid="purged-signatures">{info?.purged_signatures ?? '—'}</span></span>
              <span className="text-slate-500">Retention <span className="text-white font-bold">{info?.retention_days ?? 30}d</span></span>
            </div>
          </div>
          {closed ? (
            <button data-testid="reopen-event-btn" onClick={reopen} disabled={busy} className="btn-secondary shrink-0">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />} Reopen registration
            </button>
          ) : (
            <div className="shrink-0 flex flex-col items-start gap-2">
              <button data-testid="close-event-btn" onClick={() => setConfirm(true)} disabled={busy || isDemo} className="btn-danger !py-3.5 !text-sm">
                <Lock className="w-4 h-4" /> Close event & purge signatures
              </button>
              {isDemo && <p className="font-mono text-[10px] text-slate-500" data-testid="demo-protected-note">Disabled in the demo workspace</p>}
            </div>
          )}
        </div>
      </div>

      <div className="card p-6 flex items-start gap-4" data-testid="security-measures-card">
        <ShieldCheck className="w-5 h-5 text-cyan shrink-0 mt-0.5" />
        <div>
          <p className="font-display font-bold uppercase tracking-wider text-sm text-white">Security measures</p>
          <ul className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-slate-400">
            {['TLS on every request', 'Photos in private encrypted object storage', 'Strict tenant isolation', 'httpOnly session cookies', 'Admin-approved workspaces', 'On-server face matching, no third-party AI'].map((t) => (
              <li key={t} className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-cyan" />{t}</li>
            ))}
          </ul>
        </div>
      </div>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="bg-surface border-2 border-red-500 rounded-none shadow-[0_18px_40px_rgba(224,69,123,0.18)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight text-white">Close event and purge all signatures?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {info?.active_signatures ?? 0} face signature(s) will be permanently deleted. New registrations will be blocked until you reopen. Photos and existing galleries are kept. New uploads can no longer be matched to attendees.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="close-event-cancel-btn" className="btn-ghost rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="close-event-confirm-btn" onClick={closeEvent} disabled={busy} className="rounded-none bg-red-500 hover:bg-red-600 text-white font-display font-bold uppercase tracking-wider">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Close & purge'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
